/**
 * AdminLayout — wraps all /admin/* pages.
 *
 * On first visit, prompts for an admin email and stores it in localStorage
 * under "admin_email". That email is injected as X-Admin-Email on every
 * admin API call. The backend enforces it; this is convenience UX only.
 *
 * Mirrors prizes admin pattern exactly.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/admin' },
  { label: 'Markets', path: '/admin/markets' },
  { label: 'Risk', path: '/admin/risk' },
  { label: 'House Risk', path: '/admin/house' },
  { label: 'Linked Accounts', path: '/admin/linked-accounts' },
  { label: 'Users', path: '/admin/users' },
  { label: 'Reports', path: '/admin/reports' },
  { label: 'Partners', path: '/admin/partners' },
  { label: 'Support', path: '/admin/support' },
] as const;

export const ADMIN_EMAIL_KEY = 'admin_email';
export const ADMIN_TOKEN_KEY = 'admin_token';

export function getAdminEmail(): string {
  return localStorage.getItem(ADMIN_EMAIL_KEY) ?? '';
}

export function getAdminToken(): string {
  return localStorage.getItem(ADMIN_TOKEN_KEY) ?? '';
}

function EmailPrompt({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim() && token.trim()) {
      localStorage.setItem(ADMIN_EMAIL_KEY, email.trim());
      localStorage.setItem(ADMIN_TOKEN_KEY, token.trim());
      onDone();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
        <h2 className="mb-2 font-bold text-xl text-violet-700">Admin access</h2>
        <p className="mb-4 text-sm text-gray-500">
          Enter your admin email and the admin token provisioned from{' '}
          <strong>my-investments-admin</strong>. They are sent as{' '}
          <code className="rounded bg-gray-100 px-1 text-xs">X-Admin-Email</code> and{' '}
          <code className="rounded bg-gray-100 px-1 text-xs">X-Internal-Key</code>. Without a
          valid token the admin API rejects every request.
        </p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            ref={inputRef}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@example.com"
            className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
          />
          <input
            type="password"
            required
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Admin token"
            autoComplete="off"
            className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
          />
          <button
            type="submit"
            className="w-full rounded-lg bg-violet-600 py-2 font-semibold text-white hover:bg-violet-700"
          >
            Continue
          </button>
        </form>
      </div>
    </div>
  );
}

/** True only when BOTH the admin identity and the provisioned token are present. */
function readAdminEmail(): string | null {
  const email = localStorage.getItem(ADMIN_EMAIL_KEY);
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  return email && token ? email : null;
}

export default function AdminLayout() {
  const location = useLocation();
  // Initialise synchronously from localStorage so the Outlet gate (below) is
  // correct on first render — no flash where admin pages mount before we know
  // whether a token exists.
  const [adminEmail, setAdminEmail] = useState<string | null>(readAdminEmail);
  const [showPrompt, setShowPrompt] = useState(() => readAdminEmail() === null);

  const handleEmailSet = () => {
    // Route through readAdminEmail() so we only un-gate when BOTH email and
    // token persisted (guards a partial localStorage write). No navigate():
    // the operator stays on the page they deep-linked to (e.g. /admin/house) —
    // the re-render reveals it via the Outlet gate instead of bouncing them to
    // the dashboard.
    const email = readAdminEmail();
    setAdminEmail(email);
    setShowPrompt(email === null);
  };

  const handleChangeEmail = () => {
    localStorage.removeItem(ADMIN_EMAIL_KEY);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAdminEmail(null);
    setShowPrompt(true);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {showPrompt && <EmailPrompt onDone={handleEmailSet} />}

      {/* Sidebar */}
      <aside className="hidden w-56 shrink-0 border-r border-gray-200 bg-white lg:flex lg:flex-col">
        <div className="flex h-16 items-center border-b border-gray-200 px-5">
          <span className="font-bold text-violet-700 text-lg">My Bets Admin</span>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV_ITEMS.map((item) => {
            const active =
              item.path === '/admin'
                ? location.pathname === '/admin'
                : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-violet-50 text-violet-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
        {adminEmail && (
          <div className="border-t border-gray-200 px-4 py-3">
            <p className="truncate text-xs text-gray-400">{adminEmail}</p>
            <button
              onClick={handleChangeEmail}
              className="mt-1 text-xs text-violet-500 hover:text-violet-700"
            >
              Change email
            </button>
          </div>
        )}
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col">
        <header className="flex h-14 items-center border-b border-gray-200 bg-white px-4 lg:hidden">
          <span className="font-bold text-violet-700">My Bets Admin</span>
          {adminEmail && (
            <span className="ml-auto text-xs text-gray-400 truncate max-w-[160px]">
              {adminEmail}
            </span>
          )}
        </header>
        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          {/* Only mount the admin pages once a token is present — otherwise
              they fire admin API calls that the backend rejects (403). While
              unauthenticated, the EmailPrompt modal is shown instead. */}
          {adminEmail ? (
            <Outlet context={{ adminEmail }} />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">
              Enter your admin email and token to continue.
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
